import React, { useState } from "react";
import { Dialog, DialogContent } from "@material-ui/core";
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { stripeKey } from "../../../common/config/config";

const stripePromise = loadStripe(stripeKey);
interface PaywallModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (option: 'pay' | 'consult') => void;
  isLoading?: boolean;
  price?: number;
  currency?: string;
  clientSecret?: string;
}

const CheckoutForm = ({ clientSecret, onSubmit, onClose, price = 10, currency = "USD" }: any) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!stripe || !elements || !clientSecret) return;

    setProcessing(true);
    setError(null);

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) return;

    try {
      const payload = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
        }
      });

      const LIVE_PAYMENTS_ERROR_MSG =
        "We are currently not accepting live payments. Please contact the administrator for any concern - contact@myauctions.jp";

      const isLiveChargesError = (err: any) => {
        if (!err) return false;
        const msg = (err.message || "").toLowerCase();
        const code = (err.code || "").toLowerCase();
        return (
          code === "livemode_charges_disabled" ||
          msg.includes("live charges") ||
          msg.includes("cannot currently make live charges")
        );
      };

      if (payload.error) {
        if (isLiveChargesError(payload.error)) {
          setError(LIVE_PAYMENTS_ERROR_MSG);
        } else {
          setError(payload.error.message || 'Payment failed. Please check your card details.');
        }
        setProcessing(false);
      } else if (payload.paymentIntent && payload.paymentIntent.status === 'succeeded') {
        setError(null);
        setProcessing(false);
        onSubmit('pay');
      } else {
        setError('Payment was not completed. Please check your card details and try again.');
        setProcessing(false);
      }
    } catch (err) {
      const errorObj = err as any;
      console.error("Stripe payment error:", errorObj);
      const LIVE_PAYMENTS_ERROR_MSG =
        "We are currently not accepting live payments. Please contact the administrator for any concern - contact@myauctions.jp";
      const msg = (errorObj?.message || "").toLowerCase();
      const code = (errorObj?.code || "").toLowerCase();
      if (
        code === "livemode_charges_disabled" ||
        msg.includes("live charges") ||
        msg.includes("cannot currently make live charges")
      ) {
        setError(LIVE_PAYMENTS_ERROR_MSG);
      } else {
        setError(errorObj?.message || 'Payment processing failed. Please try again.');
      }
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ width: '100%' }}>
      <div style={{ padding: '1rem', border: '1px solid #EAEAEA', borderRadius: '8px', marginBottom: '1rem', background: '#FAFAFA' }}>
        <CardElement options={{
          hidePostalCode: true,
          style: {
            base: {
              fontSize: '16px',
              color: '#1A1A1A',
              '::placeholder': {
                color: '#999',
              },
            },
            invalid: {
              color: '#d92b3c',
            },
          },
        }} />
      </div>

      {error && (
        <div style={{ background: '#FEF2F2', border: '1px solid #F87171', color: '#991B1B', padding: '0.75rem 1rem', borderRadius: '8px', marginBottom: '1rem', fontSize: '0.85rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span>✕</span> {error}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem', marginTop: '0.5rem' }}>
        <button
          type="submit"
          disabled={!stripe || processing || !clientSecret}
          style={{ background: processing || !stripe || !clientSecret ? '#ccc' : '#d92b3c', color: '#fff', border: 'none', borderRadius: '8px', padding: '0.65rem 1.25rem', fontWeight: 'bold', fontSize: '0.85rem', cursor: processing || !stripe || !clientSecret ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}
        >
          {processing ? 'Processing...' : `Pay ${currency === 'USD' ? '$' : currency}${price.toFixed(2)}`}
        </button>
        <button
          type="button"
          onClick={onClose}
          disabled={processing}
          style={{ background: 'transparent', color: '#d92b3c', border: 'none', fontWeight: 'bold', fontSize: '0.85rem', cursor: processing ? 'not-allowed' : 'pointer', padding: 0 }}
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

const PaywallModal: React.FC<PaywallModalProps> = ({ open, onClose, onSubmit, isLoading = false, price = 10, currency = "USD", clientSecret }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ style: { borderRadius: '16px', margin: '1rem' } }}
    >
      <DialogContent style={{ padding: '2rem' }}>
        <div style={{ fontSize: '1.5rem', fontFamily: '"Playfair Display", serif', fontWeight: 'bold', color: '#1A1A1A', marginBottom: '1.5rem' }}>
          Checkout
        </div>

        <div style={{ borderBottom: '1px solid #EAEAEA', borderTop: '1px solid #EAEAEA', padding: '1rem 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <div style={{ fontSize: '0.9rem', color: '#1A1A1A', fontWeight: 600 }}>Carat Pro valuation &times; 1</div>
          <div style={{ fontSize: '0.9rem', color: '#1A1A1A', fontWeight: 'bold' }}>{currency === 'USD' ? '$' : currency}{price.toFixed(2)}</div>
        </div>

        <div style={{ fontSize: '0.75rem', color: '#666', lineHeight: 1.6, marginBottom: '1.5rem' }}>
          Securely powered by Stripe. No card details are ever collected on our servers.
        </div>

        {isLoading ? (
          <div style={{ padding: '3rem 0', textAlign: 'center' }}>
            <div style={{ color: '#d92b3c', margin: '0 auto 1rem auto', display: 'flex', justifyContent: 'center' }}>
               <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-loader-2" style={{ animation: 'spin 1s linear infinite' }}>
                 <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
               </svg>
            </div>
            <div style={{ fontSize: '1.25rem', fontWeight: 'bold', fontFamily: '"Playfair Display", serif', color: '#1A1A1A', marginBottom: '0.5rem' }}>
              Verifying payment...
            </div>
            <div style={{ fontSize: '0.85rem', color: '#666' }}>
              Please do not close this window.
            </div>
            <style>
              {`
                @keyframes spin {
                  from { transform: rotate(0deg); }
                  to { transform: rotate(360deg); }
                }
              `}
            </style>
          </div>
        ) : (
          <Elements stripe={stripePromise}>
            <CheckoutForm clientSecret={clientSecret} onSubmit={onSubmit} onClose={onClose} price={price} currency={currency} />
          </Elements>
        )}

      </DialogContent>
    </Dialog>
  );
};

export default PaywallModal;
